import { useMemo, type FC } from 'react'
import { Avatar, Box, Link as MuiLink, Typography } from '@mui/material'
import FolderIcon from '@mui/icons-material/FolderOpen'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import EventNoteIcon from '@mui/icons-material/EventNote'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import { Link } from 'react-router'
import { StatusPill, type Tone } from '~/shared/ui'
import { useRouteBasePath } from '~/shared/hooks/useRouteBasePath'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'
import { messageDateInfo } from './utils'
import { getStringInitials } from '~/shared/lib/displayText'
type ChatCommunication = Extract<
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object'],
  { kind: 'chat' }
>

type MessageRelation = ChatCommunication['relations'][number]
type RelationContent = NonNullable<MessageRelation['content_object']>
type MessageRelationKind = RelationContent['kind']
type RenderableRelation = MessageRelation & { content_object: RelationContent }

/** A type predicate rather than a plain check, so the narrowing survives `filter`. */
const isRenderable = (relation: MessageRelation): relation is RenderableRelation =>
  relation.content_object != null

/** Quoted messages are the only kind sequencing groups. */
const isQuote = (relation: RenderableRelation) => relation.content_object.kind === 'chat'

/**
 * Describes a relation item's position within its sequence.
 * 'pre'  → first of multiple (has a successor in the same sequence)
 * 'post' → last of multiple  (has a predecessor in the same sequence)
 * 'both' → middle of three or more
 * 'none' → single item (no neighbours share its sequence_id)
 */
type RelationAdjacency = 'none' | 'pre' | 'post' | 'both'

const isLastInSequence = (adjacency: RelationAdjacency) =>
  adjacency === 'none' || adjacency === 'post'

const computeAdjacency = (relations: MessageRelation[], index: number): RelationAdjacency => {
  const curr = relations[index]?.sequence_id
  const hasPrev = index > 0 && relations[index - 1]?.sequence_id === curr
  const hasNext = index < relations.length - 1 && relations[index + 1]?.sequence_id === curr
  if (hasPrev && hasNext) return 'both'
  if (hasPrev) return 'post'
  if (hasNext) return 'pre'
  return 'none'
}

interface RelationComponentProps {
  relation: RenderableRelation
  personId: number
  adjacency: RelationAdjacency
}

/**
 * Renders a single quoted chatmessagev2 relation as a blockquote-style card.
 * When adjacent (same sequence_id), removes gap and only shows "From thread"
 * footer on the last message in the sequence.
 */
const ChatMessageRelation: FC<RelationComponentProps> = ({ relation, adjacency }) => {
  // The chat panel matches the chart route itself, not a tab, so this is
  // already the chart base — no sibling-tab step needed.
  const chartBasePath = useRouteBasePath()
  const obj = relation.content_object
  // RELATION_COMPONENTS registers this only for the chat kind; the narrowing is for TS.
  if (obj.kind !== 'chat') return null

  const senderName = obj.sender_name ?? 'Unknown'
  const { messageDate } = obj.sent_at ? messageDateInfo(obj.sent_at) : { messageDate: '' }
  const isLast = isLastInSequence(adjacency)

  return (
    <Box
      sx={theme => ({
        borderLeft: `3px solid ${theme.palette.grey[200]}`,
        paddingLeft: theme.spacing(1.5),
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        // Only add bottom margin on the last item in a sequence
        marginBottom: isLast ? theme.spacing(1.5) : 0,
      })}
    >
      {/* Row 1: Avatar + sender name + sent at */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
          {getStringInitials(senderName) || '?'}
        </Avatar>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 500 }}>{senderName}</Typography>
        {messageDate && (
          <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
            {messageDate}
          </Typography>
        )}
      </Box>

      {/* Row 2: Message text in a rounded bubble */}
      {obj.text && (
        <Box
          sx={theme => ({
            backgroundColor: theme.palette.grey[100],
            borderRadius: 2,
            padding: theme.spacing(1.25, 2),
            mb: 1,
          })}
        >
          <Typography sx={{ fontSize: '1.4rem', color: 'text.primary', fontWeight: 500 }}>
            {obj.text}
          </Typography>
        </Box>
      )}

      {/* Row 3: "From thread" + folder icon + case link — only on last message in sequence */}
      {isLast && obj.case_id != null && obj.thread?.title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>From thread</Typography>
          <FolderIcon sx={{ fontSize: '1.4rem', color: 'text.secondary' }} />
          <Link
            to={`${chartBasePath}/todos?caseId=${obj.case_id}`}
            style={{ fontSize: '1.1rem', textDecoration: 'underline', color: 'inherit' }}
          >
            {obj.thread.title}
          </Link>
        </Box>
      )}
    </Box>
  )
}

type BookingWidgetContent = Extract<RelationContent, { kind: 'booking_attempt' }>

// Total over the server enum, so a new state fails the build here rather than
// rendering an unlabeled row.
const WIDGET_STATE_PILLS: Record<
  BookingWidgetContent['widget_state'],
  { label: string; tone: Tone }
> = {
  ready_to_book: { label: 'Ready to book', tone: 'warning' },
  expired: { label: 'Expired booking', tone: 'neutral' },
  booked: { label: 'Booked', tone: 'success' },
  completed: { label: 'Completed', tone: 'success' },
  missed: { label: 'Missed appointment', tone: 'error' },
  canceled: { label: 'Canceled', tone: 'error' },
}

/**
 * Renders the booking widget the member was offered, inert. Staff see what the member
 * saw and where it stands — the same state enum the member's card renders; there is
 * nothing here to click, because only the member can book from their own offer.
 */
const BookingWidgetRelation: FC<RelationComponentProps> = ({ relation }) => {
  const obj = relation.content_object
  // RELATION_COMPONENTS registers this only for the booking_attempt kind; the narrowing is for TS.
  if (obj.kind !== 'booking_attempt') return null

  const { appointment, appointment_type: appointmentType, widget_state: widgetState } = obj
  // The group, matching what the member's card shows; an ungrouped type falls back to
  // its own title.
  const title = appointmentType.group_title ?? appointmentType.display_title
  // The pill carries the state, so the detail line carries only the facts beside it.
  const detail = appointment
    ? `With ${appointment.provider_name ?? 'a Firefly provider'} · ${messageDateInfo(appointment.start).messageDate}`
    : `Recommended visit · ${appointmentType.duration} min`
  const pill = WIDGET_STATE_PILLS[widgetState]
  const StateIcon = !appointment
    ? EventNoteIcon
    : widgetState === 'canceled' || widgetState === 'missed'
      ? EventBusyIcon
      : EventAvailableIcon

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 1,
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1.5),
      })}
    >
      <StateIcon sx={{ fontSize: '1.6rem', color: 'text.secondary' }} />
      <Box>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 500 }}>{title}</Typography>
        <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>{detail}</Typography>
      </Box>
      <Box sx={{ ml: 'auto' }}>
        <StatusPill tone={pill.tone} label={pill.label} />
      </Box>
    </Box>
  )
}

/**
 * Renders the plan document a grounded benefits answer was built from, as an external
 * link. The CRM has no in-app document viewer and needs none: the Benefit Documents tab
 * already opens plan documents from this same server-resolved `link` in a new tab, and
 * reusing its glyph keeps the two surfaces reading as the same thing.
 */
const PolicyDocumentSourceRelation: FC<RelationComponentProps> = ({ relation }) => {
  const obj = relation.content_object
  // RELATION_COMPONENTS registers this only for the policy_document kind; the narrowing is for TS.
  if (obj.kind !== 'policy_document') return null

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        // Hugs its label: this sits under every grounded answer, where a full-width bar
        // would carry more weight than the reference deserves.
        width: 'fit-content',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 1,
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1.5),
      })}
    >
      <InsertDriveFileOutlinedIcon sx={{ fontSize: '1.6rem', color: 'text.secondary' }} />
      {obj.link ? (
        <MuiLink
          href={obj.link}
          target="_blank"
          rel="noopener noreferrer"
          // `ChatMessage` wraps its children in a click handler that calls preventDefault
          // and selects the message for quoting. This anchor opens through the browser's
          // default action, which that cancels — so the click stops here, as the message
          // text does to stay selectable.
          onClick={event => event.stopPropagation()}
          sx={{ fontSize: '1.2rem', fontWeight: 500 }}
        >
          {obj.name}
        </MuiLink>
      ) : (
        // A document the server resolved no target for is named, not linked — the same
        // thing the Benefit Documents row does with a blank link.
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 500 }}>{obj.name}</Typography>
      )}
    </Box>
  )
}

type SelfServiceWidgetContent = Extract<RelationContent, { kind: 'navigation_self_service' }>

// Total over the server enum, so a new state fails the build here rather than
// rendering an unlabeled row.
const SELF_SERVICE_STATE_PILLS: Record<
  SelfServiceWidgetContent['widget_state'],
  { label: string; tone: Tone }
> = {
  searching: { label: 'Finding providers', tone: 'info' },
  ready: { label: 'Ready to pick', tone: 'warning' },
  provider_selected: { label: 'Provider chosen', tone: 'success' },
  handed_off: { label: 'Handed off', tone: 'neutral' },
  expired: { label: 'Expired', tone: 'neutral' },
}

/**
 * Renders the Care Key self-service card the member was offered, inert. Staff read
 * where the request stands; they act on it from the steerage view, which is where
 * the specialty, the lock and the suggested providers are editable.
 */
const NavigationSelfServiceRelation: FC<RelationComponentProps> = ({ relation }) => {
  const obj = relation.content_object
  // RELATION_COMPONENTS registers this only for the navigation_self_service kind; the
  // narrowing is for TS.
  if (obj.kind !== 'navigation_self_service') return null

  const {
    specialty_label: specialtyLabel,
    selected_provider_name: selectedProviderName,
    widget_state: widgetState,
  } = obj
  const pill = SELF_SERVICE_STATE_PILLS[widgetState]
  // The pill carries the state, so the detail line carries only the facts beside it.
  const detail = selectedProviderName
    ? `Chose ${selectedProviderName}`
    : 'Care Key · member picks a provider in the app'

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 1,
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1.5),
      })}
    >
      <LocalHospitalOutlinedIcon sx={{ fontSize: '1.6rem', color: 'text.secondary' }} />
      <Box>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 500 }}>
          {specialtyLabel ?? 'Specialist referral'}
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>{detail}</Typography>
      </Box>
      <Box sx={{ ml: 'auto' }}>
        <StatusPill tone={pill.tone} label={pill.label} />
      </Box>
    </Box>
  )
}

/**
 * Maps kind strings to their relation renderer components.
 * Add new entries here when new relation types are supported. A kind with no entry
 * renders nothing, which is what the CRM does with a kind it cannot draw.
 */
const RELATION_COMPONENTS: Partial<Record<MessageRelationKind, FC<RelationComponentProps>>> = {
  chat: ChatMessageRelation,
  booking_attempt: BookingWidgetRelation,
  policy_document: PolicyDocumentSourceRelation,
  navigation_self_service: NavigationSelfServiceRelation,
}

interface ChatMessageRelationsProps {
  relations: MessageRelation[]
  personId: number
}

/**
 * Iterates over message_relations and dispatches each to the appropriate
 * component based on content_type. Computes sequence adjacency so items
 * know whether they are first/last in a run.
 */
export const ChatMessageRelations: FC<ChatMessageRelationsProps> = ({ relations, personId }) => {
  // A relation the endpoint could not hydrate — a kind outside its map, or a target
  // that is gone — arrives with a null content_object. Drop those up front: they
  // render nothing, and counting them in adjacency would also split a run of quotes
  // they sit between.
  const renderableRelations = useMemo(() => relations.filter(isRenderable), [relations])

  // Keyed by the relation itself, so nothing has to line up by index or by a content id,
  // which is only unique inside one kind's table. Sequencing is a quoted-message idea — the
  // server numbers quotes and nothing else — so adjacency is computed among the quotes
  // alone, which is what stops a widget between two of them breaking their run.
  const adjacencyByQuote = useMemo(() => {
    const quotes = renderableRelations.filter(isQuote)
    return new Map(quotes.map((quote, index) => [quote, computeAdjacency(quotes, index)]))
  }, [renderableRelations])

  if (!renderableRelations.length) return null

  return (
    <Box sx={{ mt: 0.5, mb: 0.5 }}>
      {renderableRelations.map(relation => {
        const obj = relation.content_object
        const Component = RELATION_COMPONENTS[obj.kind]
        if (!Component) return null
        return (
          <Component
            key={`${obj.kind}-${obj.id}`}
            relation={relation}
            personId={personId}
            adjacency={adjacencyByQuote.get(relation) ?? 'none'}
          />
        )
      })}
    </Box>
  )
}
